import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, MONTHS_IT, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer } from "@phosphor-icons/react";

const n2 = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-sm border border-border bg-card p-3" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-head font-bold tabular-nums">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{children}</th>
);

export default function MyWeights() {
  const now = new Date();
  const [tab, setTab] = useState("day");
  const [day, setDay] = useState(today());
  const [dayData, setDayData] = useState(null);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  const loadDay = useCallback(async () => {
    try { setDayData((await api.get("/weights/my/day", { params: { day } })).data); }
    catch (e) { setDayData(null); toast.error(errMsg(e)); }
  }, [day]);

  const loadMonth = useCallback(async () => {
    try { setData((await api.get("/weights/my/detail", { params: { year, month } })).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [year, month]);

  const [split, setSplit] = useState(null);

  const loadSplit = useCallback(async () => {
    try { setSplit((await api.get("/weights/my/month-detail", { params: { year, month } })).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [year, month]);

  useEffect(() => {
    if (tab === "day") loadDay();
    else if (tab === "split") loadSplit();
    else loadMonth();
  }, [tab, loadDay, loadMonth, loadSplit]);

  return (
    <div className="space-y-3" data-testid="weights-my-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">I miei pesi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pesi giornalieri con il PDF della linea, oppure il riepilogo del mese giorno per giorno.
          </p>
        </div>
        <div className="flex gap-2">
          {[["day", "Pesi Giornalieri"], ["split", "Divisione del Mese"], ["month", "Pesi Mensili"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={tab === k ? "default" : "outline"}
              className="rounded-sm font-semibold" data-testid={`my-tab-${k}`} onClick={() => setTab(k)}>
              {l}
            </Button>
          ))}
        </div>
      </div>

      {tab === "day" && (
        <>
          <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
              className="h-9 w-[170px] rounded-sm" data-testid="my-day-picker" />
            <span className="text-sm text-muted-foreground">
              Seleziona la giornata: vedi le linee su cui hai lavorato, i totali dei colleghi e il PDF della linea.
            </span>
          </div>

          {dayData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Kpi label="Miei kg del giorno" value={n2(dayData.total_mine)} testid="my-day-total" />
                <Kpi label="Linee lavorate" value={dayData.lines.length} testid="my-day-lines-count" />
                <Kpi label="Giornata" value={itDate(dayData.date)} testid="my-day-date" />
              </div>

              {dayData.lines.length === 0 && (
                <div className="rounded-sm border border-border bg-card p-8 text-center text-muted-foreground"
                  data-testid="my-day-empty">
                  Nessuna lavorazione a te assegnata in questa giornata
                </div>
              )}

              {dayData.lines.map((l) => (
                <div key={l.line} className="rounded-sm border border-border bg-card"
                  data-testid={`my-day-line-${l.line}`}>
                  <div className="mobile-stack flex flex-wrap items-center gap-2 p-3 border-b border-border">
                    <h2 className="font-head font-semibold text-base md:text-lg">Linea {l.line}</h2>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-primary/10 text-primary tabular-nums">
                      miei {n2(l.mine)} kg
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      totale linea {n2(l.weight)} kg
                    </span>
                    <Button size="sm" variant="outline" className="h-8 rounded-sm ml-auto"
                      data-testid={`my-day-pdf-${l.line}`}
                      onClick={() => download(`/weights/my/day-pdf?day=${dayData.date}&line=${l.line}`,
                        `${dayData.date} - ${l.line}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                      <Printer size={14} className="mr-1.5" /> PDF LINEA {l.line}
                    </Button>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-secondary"><tr>
                      <Th>Collega sulla linea</Th><Th>Kg assegnati</Th>
                    </tr></thead>
                    <tbody>
                      {l.operators.map((o) => (
                        <tr key={o.employee_id} className="border-t border-border"
                          data-testid={`my-day-op-${l.line}-${o.employee_id}`}>
                          <td className="px-3 py-1.5">{o.name}</td>
                          <td className="px-3 py-1.5 tabular-nums font-semibold">{n2(o.weight)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {tab === "split" && (
        <div className="space-y-3" data-testid="my-split">
          <div className="flex flex-wrap items-end gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-40 rounded-sm" data-testid="my-split-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-28 rounded-sm" data-testid="my-split-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear() - 1, now.getFullYear()].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
            </Select>
          </div>

          {split && (
            <div className="grid gap-3 sm:grid-cols-3">
              {[["Giornate lavorate", split.worked_days], ["Kg del mese", n2(split.total_kg)],
                ["Quota media di cottimo", `${n2(split.avg_percent)} %`]].map(([l, v]) => (
                <div key={l} className="rounded-sm border border-border bg-card p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="mt-1 text-2xl font-head font-bold tabular-nums">{v}</div>
                </div>
              ))}
            </div>
          )}

          {(split?.days || []).map((d) => (
            <div key={d.date} className="rounded-sm border border-border bg-card" data-testid={`my-split-day-${d.date}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-secondary/50 px-3 py-2">
                <span className="font-head font-semibold">{itDate(d.date)}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">{d.lines.join(" · ")}</span>
                </span>
                <span className="text-sm">
                  <b className="tabular-nums">{n2(d.kg)} kg</b>
                  <span className="ml-2 rounded-sm bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary tabular-nums">
                    {n2(d.percent)} % del giorno
                  </span>
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/30"><tr>
                    {["Lavorazione", "Linea", "Kg lavorazione", "Quota", "Kg a me"].map((h) => (
                      <Th key={h}>{h}</Th>))}
                  </tr></thead>
                  <tbody>
                    {d.items.map((i, k) => (
                      <tr key={`${i.code}-${i.line || ""}-${k}`} className="border-t border-border">
                        <td className="px-3 py-1.5">
                          <b>{i.code}</b> <span className="text-muted-foreground">{i.description}</span>
                        </td>
                        <td className="px-3 py-1.5 text-xs text-muted-foreground">{i.line}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(i.weight)}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(i.percent)} %</td>
                        <td className="px-3 py-1.5 tabular-nums font-semibold">{n2(i.kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {split && split.days.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="my-split-empty">
              Nessuna lavorazione assegnata in questo mese.
            </p>
          )}
        </div>
      )}

      {tab === "month" && (
        <>
          <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-[140px] rounded-sm" data-testid="my-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-[105px] rounded-sm" data-testid="my-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="rounded-sm font-semibold ml-auto" data-testid="my-print"
              onClick={() => download(`/weights/my/pdf?year=${year}&month=${month}`, `miei_kg_${year}_${month}.pdf`)
                .catch((e) => toast.error(errMsg(e)))}>
              <Printer size={15} className="mr-1.5" /> Stampa riepilogo mese
            </Button>
          </div>

          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Kpi label="Kg lavorati nel mese" value={n2(data.total)} testid="my-total" />
                <Kpi label="Giornate" value={data.days.length} testid="my-days" />
              </div>

              <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
                <table className="w-full text-sm" data-testid="my-day-lines-table">
                  <thead className="bg-secondary"><tr>
                    <Th>Giornata</Th><Th>Kg SUINO</Th><Th>Kg BOVINO</Th><Th>Kg altre linee</Th>
                    <Th>Totale kg</Th><Th>Stampa</Th>
                  </tr></thead>
                  <tbody>
                    {(data.day_pivot || []).map((x) => (
                      <tr key={x.date} className="border-t border-border hover:bg-secondary/40"
                        data-testid={`my-dl-${x.date}`}>
                        <td className="px-3 py-1.5">{itDate(x.date)}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(x.SUINO)}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(x.BOVINO)}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(x.ALTRE)}</td>
                        <td className="px-3 py-1.5 tabular-nums font-semibold">{n2(x.total)}</td>
                        <td className="px-3 py-1.5 flex gap-2">
                          {["SUINO", "BOVINO"].filter((ln) => x[ln] > 0).map((ln) => (
                            <button key={ln} className="text-xs text-primary hover:underline font-semibold"
                              data-testid={`my-dl-pdf-${x.date}-${ln}`}
                              onClick={() => download(`/weights/my/day-pdf?day=${x.date}&line=${ln}`, `${x.date} - ${ln}.pdf`)
                                .catch((e) => toast.error(errMsg(e)))}>
                              <Printer size={13} className="inline mr-1" />{ln}
                            </button>
                          ))}
                        </td>
                      </tr>
                    ))}
                    {(data.day_pivot || []).length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground" data-testid="my-empty">
                        Nessun peso assegnato in questo mese
                      </td></tr>
                    )}
                    {(data.day_pivot || []).length > 0 && (
                      <tr className="border-t-2 border-border bg-secondary/50 font-bold">
                        <td className="px-3 py-1.5">TOTALE</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(data.day_pivot.reduce((s, x) => s + x.SUINO, 0))}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(data.day_pivot.reduce((s, x) => s + x.BOVINO, 0))}</td>
                        <td className="px-3 py-1.5 tabular-nums">{n2(data.day_pivot.reduce((s, x) => s + x.ALTRE, 0))}</td>
                        <td className="px-3 py-1.5 tabular-nums" data-testid="my-month-total">{n2(data.total)}</td>
                        <td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
